import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { useToast } from "hooks/toast";

import { hideLoading, showLoading } from "store/modules/loading/actions";
import { setUserCreateProfile } from "store/modules/profileCreate/actions";
import { clearQueues } from "store/modules/profilePermissionQueue/actions/actions";
import { useAppSelector } from "store/rootReducer";

import ProfileBodyModal from "features/ProfileBodyModal";

import { AdminArea } from "components/Body/AdminArea";
import { Drawer } from "components/Drawer";

import { TipoParticipanteData } from "services/participantes";
import { profilesService } from "services/profiles";
import { IProfile } from "services/profiles/interfaces";
import { listSearch } from "services/utils";

import { LogOperation } from "utils/entities/logOperation";
import { FORMS_ADMIN } from "utils/entities/permission";
import { stringFormat } from "utils/stringFormat";

import { LoadingProvider } from "./profile-hook";
import { Profile } from "./types";

type ISituation = true | false | undefined;

const getConfidentialityLevel = (profile?: any) => {
  const value = profile?.confidentialityLevel ?? 0;
  const level = Number(value);

  return Number.isFinite(level) ? level : 0;
};

export function Profiles() {
  const { t } = useTranslation();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [confidentialityLevel, setConfidentialityLevel] = useState(0);

  const [isEdit, setIsEdit] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [actived, setActived] = useState(true);
  const { loading } = useAppSelector((state: any) => state.loading);
  const { accessToken } = useAppSelector((state) => state.auth);
  const [adminSwitch, setAdminSwitch] = useState<number>(0);
  const [idProfile, setIdProfile] = useState<any>();
  const [requiredName, setRequiredName] = useState(false);
  const [requiredDescription, setRequiredDescription] = useState(false);
  const [situation, setSituation] = useState<ISituation>(undefined);
  const [search, setSearch] = useState("");
  const [listProfiles, setListProfiles] = useState<Array<IProfile>>(
    [] as Array<IProfile>,
  );

  const { user } = useAppSelector((state) => state.user);
  const { pendingCreates, pendingDeletes } = useSelector(
    (state: any) => state.profilePermissionQueue,
  );

  const { addToast } = useToast();
  const dispatch = useDispatch();

  const editAttendantType = (participantItem: TipoParticipanteData) => {
    dispatch(clearQueues());
    setActived(participantItem?.active);
    setIsEdit(true);
    setIdProfile(participantItem?.id);
    let newData: TipoParticipanteData | undefined;
    listProfiles.forEach((item) => {
      if (item?.name === participantItem?.name) {
        newData = item;
      }
    });
    setAdminSwitch(participantItem.type);
    setConfidentialityLevel(
      getConfidentialityLevel(newData ?? participantItem),
    );

    dispatch(
      setUserCreateProfile({
        participantItem: {
          active: participantItem.active,
          customerId: participantItem.customerId,
          id: participantItem?.id,
          name: participantItem?.name,
          description: newData?.description,
          confidentialityLevel: getConfidentialityLevel(
            newData ?? participantItem,
          ),
        },
      }),
    );
  };

  const closeModal = () => {
    setIsEdit(false);
    setOpenModal(false);
    setActived(true);
    setName("");
    setDescription("");
    setConfidentialityLevel(0);
    localStorage.removeItem("newList");
    localStorage.removeItem("newUpdateList");
    localStorage.removeItem("dataProfile");
    dispatch(clearQueues());
    dispatch(
      setUserCreateProfile({
        participantItem: {
          id: 0,
        },
      }),
    );
  };

  async function editProfile() {
    if (!accessToken) {
      return;
    }

    if (stringFormat.isEmptyOrSpaces(name)) {
      setRequiredName(true);
    } else {
      setRequiredName(false);
    }

    if (stringFormat.isEmptyOrSpaces(description)) {
      setRequiredDescription(true);
    } else {
      setRequiredDescription(false);
    }

    if (
      stringFormat.isEmptyOrSpaces(name) ||
      stringFormat.isEmptyOrSpaces(description)
    ) {
      addToast({
        type: "warning",
        title: t("profiles.title"),
        description: t("profiles.invalidProfileDescription"),
      });
      return;
    }

    dispatch(showLoading());

    try {
      const response = await profilesService.changeProfileNomeEDescricao(
        accessToken,
        idProfile,
        name,
        description,
        adminSwitch,
        actived,
        confidentialityLevel,
      );

      await applyPermissionChanges();

      if (response.status) {
        addToast({
          type: "success",
          title: t(
            isEdit ? "profiles.profileUpdated" : "profiles.profileCreated",
          ),
          description: t(
            isEdit
              ? "profiles.profileUpdatedSuccess"
              : "profiles.profileCreatedSuccess",
          ),
        });
        await getProfiles();
        await LogOperation.postLogOperation("Profile", 2, idProfile);
        closeModal();
      } else {
        addToast({
          type: "error",
          title: t("common.error"),
          description: t("profiles.profileUpdateError"),
        });
      }
    } catch (error: any) {
      addToast({
        type: "error",
        title: t("common.error"),
        description: t("profiles.profileUpdateError"),
      });
    }
    dispatch(hideLoading());
  }

  const getProfiles = async () => {
    try {
      const { body, status } = await profilesService.getAllByCustomerId(
        listSearch(situation),
      );

      if (status && body.length) {
        setListProfiles(body);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handleSitualtion = (value: string | number) => {
    const n: number = typeof value === "string" ? parseInt(value, 10) : value;

    switch (n) {
      case 1:
        setSituation(true);
        break;
      case 0:
        setSituation(false);
        break;
      case 2:
        setSituation(undefined);
        break;
      default:
        setSituation(undefined);
        break;
    }
  };

  const filteredData = listProfiles?.filter((item: any) =>
    item?.name.toLocaleLowerCase().includes(search.toLocaleLowerCase()),
  );
  const situationFilteredData = filteredData?.filter(
    (item: any) => item.active === situation,
  );

  useEffect(() => {
    (async () => {
      dispatch(showLoading());
      await getProfiles();
      dispatch(hideLoading());
    })();
  }, [idProfile, situation]);

  function getProfileObjectToSave(): Profile | null {
    if (!user || !name) {
      return null;
    }

    const profile = {
      customerId: user.customerId,
      name,
      description,
      active: actived,
      type: adminSwitch,
      confidentialityLevel,
      device: LogOperation.getDevice(),
      ip: LogOperation.getMachineIP(),
      logPoliceUnitId: LogOperation.getPoliceUnitId(),
      logUserId: LogOperation.getUserId(),
    };

    return profile;
  }

  function groupActionsWithPermissions() {
    const actionList = pendingCreates;

    if (!actionList || actionList.length <= 0) return [];

    function groupByCustomerId(actionList: any[]) {
      const groupedData = [];
      const customerIdMap: any = {};

      for (const item of actionList) {
        const customerId = Number(item.optionId);
        const action = { systemActionId: Number(item.actionId) };

        if (customerIdMap[customerId]) {
          customerIdMap[customerId].actionList.push(action);
        } else {
          const customerData = {
            systemObjectCustomerId: customerId,
            actionList: [action],
          };
          customerIdMap[customerId] = customerData;
          groupedData.push(customerData);
        }
      }

      return groupedData;
    }

    const groupedData = groupByCustomerId(actionList);

    return groupedData;
  }

  //------------------------------------------------------------------------------------------------------------------------

  function validateObjectProfileWithPermissionsAndActions() {
    const proficonstoSave = getProfileObjectToSave();

    const isNameEmpty =
      name === undefined || stringFormat.isEmptyOrSpaces(name);
    const isDescriptionEmpty =
      description === undefined || stringFormat.isEmptyOrSpaces(description);

    setRequiredName(isNameEmpty);
    setRequiredDescription(isDescriptionEmpty);

    if (
      !proficonstoSave?.active ||
      !proficonstoSave?.customerId ||
      !proficonstoSave?.description ||
      !proficonstoSave?.logPoliceUnitId ||
      !proficonstoSave?.logUserId ||
      isNameEmpty ||
      isDescriptionEmpty
    ) {
      return false;
    }

    const permissionsAndActions = groupActionsWithPermissions();

    if (!permissionsAndActions || permissionsAndActions.length <= 0) {
      return false;
    }

    const isEmpty = (value: unknown): boolean =>
      value == null || (typeof value === "string" && value.trim() === "");
    for (const item of permissionsAndActions) {
      for (const action of item.actionList) {
        if (Object.values(action).some(isEmpty)) {
          return false;
        }
      }
    }

    return true;
  }

  async function attachPermissionsToProfile(profileData: any) {
    const permissionsAndActions = groupActionsWithPermissions();
    if (!permissionsAndActions || permissionsAndActions.length === 0) return;

    for (const systemObjectCustomer of permissionsAndActions) {
      const systemObjectUserCustomerProfile =
        await createSystemObjectUserCustomerProfile(
          profileData,
          systemObjectCustomer.systemObjectCustomerId,
        );

      if (!systemObjectUserCustomerProfile?.status) {
        addToast({
          type: "error",
          title: t("common.error"),
          description: t("profiles.profileRelationError"),
        });
        continue;
      }

      const rawArray = (systemObjectCustomer.actionList || []).map(
        (item: any) => ({
          systemActionId: Number(item.systemActionId),
          soUserCustomerProfileId: Number(systemObjectUserCustomerProfile.data),
        }),
      );
      const seen = new Set<number>();
      const arrayActionWithRelation = rawArray.filter((a) => {
        if (!a.systemActionId || !a.soUserCustomerProfileId) return false;
        if (seen.has(a.systemActionId)) return false;
        seen.add(a.systemActionId);
        return true;
      });

      const resp = await createActionsByProfileRelated(arrayActionWithRelation);
      if (!resp?.status) {
        addToast({
          type: "error",
          title: t("common.error"),
          description: t("profiles.profileActionsError"),
        });
        break;
      }
    }
  }

  async function createProfile() {
    dispatch(showLoading());

    if (!validateObjectProfileWithPermissionsAndActions()) {
      addToast({
        type: "warning",
        title: t("profiles.title"),
        description: t("profiles.invalidProfileDescription"),
      });
      dispatch(hideLoading());
      return;
    }

    const proficonstoSave = getProfileObjectToSave();

    if (!accessToken) {
      dispatch(hideLoading());
      return;
    }

    try {
      const { status, data, message } = await profilesService.createProfile(
        accessToken,
        proficonstoSave,
      );

      if (status) {
        addToast({
          type: "success",
          title: t(
            isEdit ? "profiles.profileUpdated" : "profiles.profileCreated",
          ),
          description: t(
            isEdit
              ? "profiles.profileUpdatedSuccess"
              : "profiles.profileCreatedSuccess",
          ),
        });
        await getProfiles();
        closeModal();

        await attachPermissionsToProfile(data);
        setIdProfile(data);
      } else {
        addToast({
          type: "warning",
          title: t("common.warning"),
          description: message,
        });
      }
    } catch (error) {
      addToast({
        type: "error",
        title: t("common.error"),
        description: t("profiles.profileNotCreated"),
      });
    }
    dispatch(hideLoading());
  }

  /**
   * Essa criação vai retornar o id do relacionamento do perfil com a devida action
   */
  async function createSystemObjectUserCustomerProfile(
    userCustomerProfileId: number,
    systemObjectCustomerId: number,
  ) {
    if (!accessToken) {
      return;
    }

    try {
      const { status, data } =
        await profilesService.createRelationProfileAction(
          accessToken,
          userCustomerProfileId,
          systemObjectCustomerId,
        );

      if (!status) {
        addToast({
          type: "error",
          title: t("common.error"),
          description: t("profiles.profileActionRelationError"),
        });
        return {
          status: false,
          data: null,
        };
      }

      return {
        status: true,
        data: data,
      };
    } catch (error) {
      addToast({
        type: "error",
        title: t("common.error"),
        description: t("profiles.profileNotCreated"),
      });
      return {
        status: false,
        data: null,
      };
    }
  }

  /**
   * Essa criação vai retornar o id do relacionamento do perfil com a devida action
   */
  async function createActionsByProfileRelated(actions: any) {
    if (!accessToken) {
      return;
    }

    try {
      const { status, data } =
        await profilesService.createActionByProfileRelation(
          accessToken,
          actions,
        );

      if (!status) {
        addToast({
          type: "error",
          title: t("common.error"),
          description: t("profiles.profileActionRelationError"),
        });
        return {
          status: false,
          data: null,
        };
      }

      return {
        status: true,
        data: data,
      };
    } catch (error) {
      addToast({
        type: "error",
        title: t("common.error"),
        description: t("profiles.profileNotCreated"),
      });
      return {
        status: false,
        data: null,
      };
    }
  }

  const inputRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  function clear() {
    setIsEdit(false);
    setDescription("");
    setName("");
    setOpenModal(false);
    setRequiredName(false);
    setRequiredDescription(false);
    closeModal();
  }

  function openModalClearAdd() {
    dispatch(clearQueues());
    clear();
    setOpenModal(true);
  }

  const handleAdminSwitchChange = (active: boolean) => {
    setAdminSwitch(active ? 2 : 0);
  };

  async function applyPermissionChanges() {
    if (!accessToken || !idProfile) return;

    const toCreate = pendingCreates;
    const toDelete = pendingDeletes;
    const grouped = new Map<
      number,
      { soId: number | null; actions: Set<number> }
    >();
    for (const it of toCreate) {
      const optionId = Number(it.optionId);
      const actionId = Number(it.actionId);
      if (!grouped.has(optionId)) {
        grouped.set(optionId, {
          soId: it.soUserCustomerProfileId ?? null,
          actions: new Set(),
        });
      }
      grouped.get(optionId)!.actions.add(actionId);
    }

    for (const [optionId, info] of grouped) {
      let soId = info.soId;
      if (!soId) {
        const rel = await profilesService.createRelationProfileAction(
          accessToken,
          Number(idProfile),
          optionId,
        );
        if (!rel?.status || !rel.data) continue;
        soId = Number(rel.data);
      }

      const payload = Array.from(info.actions).map((a) => ({
        soUserCustomerProfileId: Number(soId),
        systemActionId: Number(a),
      }));

      await profilesService.createActionByProfileRelation(accessToken, payload);
    }

    for (const del of toDelete) {
      const id =
        del.systemObjectUserCustomerProfileActionId ??
        del.soUserCustomerProfileActionId ??
        null;
      if (id) {
        await profilesService.deleteCustom(accessToken, Number(id));
      }
    }

    dispatch(clearQueues());
  }

  return (
    <>
      <AdminArea
        title={t("profiles.title")}
        InputSearchLabel={t("common.searchBy")}
        InputSearchOnChange={(text) => setSearch(text)}
        searchText={search}
        loading={loading}
        list={situation === undefined ? filteredData : situationFilteredData}
        headerAddButtonOnClick={() => openModalClearAdd()}
        headerTrashButtonOnClick={() => {}}
        onClick={(item: any) => editAttendantType(item)}
        setItemReturnClick={null}
        setOpenModal={setOpenModal}
        setIsEdit={setIsEdit}
        permissionName={FORMS_ADMIN.PROFILE}
        onChangeSituation={handleSitualtion}
      />
      <Drawer
        open={isEdit || openModal}
        onClose={() => clear()}
        onApply={isEdit ? editProfile : createProfile}
        title={isEdit ? t("profiles.editProfile") : t("profiles.createProfile")}
        paddingLeft={30}
        paddingRight={30}
      >
        <LoadingProvider>
          <ProfileBodyModal
            onChangeSwitch={() => setActived((v) => !v)}
            name={name}
            description={description}
            setName={setName}
            setDescription={setDescription}
            active={actived}
            formsListData={() => {}}
            nameError={requiredName}
            descriptionError={requiredDescription}
            isEdit={isEdit}
            userCustomerProfile={idProfile}
            adminSwitchValue={adminSwitch}
            onAdminSwitchChange={handleAdminSwitchChange}
            confidentialityLevel={confidentialityLevel}
            setConfidentialityLevel={setConfidentialityLevel}
          />
        </LoadingProvider>
      </Drawer>
    </>
  );
}
